import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";

// Global USD->INR FX control (Fixed vs Live). Shown in the top bar on every page. Changing it
// saves the company-wide setting, busts server caches, and reloads so every ₹ figure recomputes
// at the new rate. Fixed defaults to 80; GBP always stays on the live daily rate.
export default function FxControl() {
  const [s, setS] = useState(null);
  const [draft, setDraft] = useState("80");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/fx/settings").then((r) => { setS(r.data); setDraft(String(r.data.usd_inr_fixed ?? 80)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const apply = async (payload, msg) => {
    setBusy(true);
    try {
      await api.post("/fx/settings", payload);
      toast.success(msg + " — refreshing all figures…");
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update FX rate");
      setBusy(false);
    }
  };

  const refreshLive = async () => {
    setBusy(true);
    try {
      const r = await api.post("/fx/refresh-live");
      toast.success(`Live rate refreshed: ₹${r.data.live_usd_inr}/$ — reloading…`);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to refresh live rate");
      setBusy(false);
    }
  };

  if (!s) return null;
  const isFixed = s.mode === "fixed";
  const btn = (active) => ({
    padding: "4px 9px", fontSize: 11, fontWeight: 700, borderRadius: 0, border: "none",
    cursor: "pointer", color: active ? "white" : "var(--omh-fg)",
    background: active ? "var(--omh-primary)" : "transparent",
  });

  return (
    <div data-testid="fx-control"
      title={`USD→INR. Live (ECB): ₹${s.live_usd_inr ?? "?"}/$ as of ${s.live_asof || "?"}. GBP always uses the live rate.`}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginRight: 6,
        border: "1px solid var(--omh-border)", borderRadius: 4, padding: "2px 4px 2px 8px" }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3, color: "#cbd5e1" }}>USD→₹</span>
      <div style={{ display: "inline-flex", border: "1px solid var(--omh-border)", borderRadius: 4, overflow: "hidden" }}>
        <button data-testid="fx-mode-fixed" style={{ ...btn(isFixed), borderRight: "1px solid var(--omh-border)" }}
          disabled={busy}
          onClick={() => { if (!isFixed) apply({ mode: "fixed", usd_inr_fixed: Number(draft) || 80 }, "Fixed FX set"); }}>
          FIXED
        </button>
        <button data-testid="fx-mode-live" style={btn(!isFixed)} disabled={busy}
          onClick={() => { if (isFixed) apply({ mode: "live" }, "Switched to live FX"); }}>
          LIVE
        </button>
      </div>
      {isFixed ? (
        <>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#ffffff" }}>₹</span>
          <input data-testid="fx-fixed-input" type="number" step="0.25" min="1" value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && Number(draft) > 0) apply({ mode: "fixed", usd_inr_fixed: Number(draft) }, `FX set to ₹${draft}/$`); }}
            style={{ width: 58, fontSize: 12, fontWeight: 700, padding: "2px 4px",
              border: "1px solid var(--omh-border)", borderRadius: 4, textAlign: "right",
              color: "#0f172a", background: "#ffffff" }} />
          {Number(draft) > 0 && Number(draft) !== s.usd_inr_fixed && (
            <button data-testid="fx-fixed-apply" disabled={busy}
              onClick={() => apply({ mode: "fixed", usd_inr_fixed: Number(draft) }, `FX set to ₹${draft}/$`)}
              style={{ ...btn(true), borderRadius: 4, padding: "3px 8px" }}>Set</button>
          )}
        </>
      ) : (
        <>
          <span data-testid="fx-effective-rate" style={{ fontSize: 12, fontWeight: 700, color: "#ffffff" }}>
            ₹{s.effective_usd_inr ?? "?"}
          </span>
          <button data-testid="fx-refresh-live" title="Refresh live rate now" disabled={busy}
            onClick={refreshLive}
            className="omh-icon-btn" style={{ padding: 3 }}>
            <ArrowsClockwise size={13} weight="bold" style={busy ? { animation: "spin 1s linear infinite" } : undefined} />
          </button>
        </>
      )}
    </div>
  );
}
